import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { TamilNameField } from '@/components/TamilNameField'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import {
  useEditPackage,
  useEditableVersion,
  useHalls,
  useServiceTypes,
  useSessionTypes,
  useOutlets,
  useSavePackageVersion,
} from '@/api/hooks'
import type { CateringPackage } from '@/api/types'
import { ErrorBanner, formatMoney } from '@/components/common'
import { DishPicker, useDishMemory, type Dish } from './DishPicker'
import { PortionList } from './PortionList'
import { nearestPortion, WHOLE_PORTION } from './portions'


type ItemDraft = {
  itemNumber: number
  itemLabel: string
  /** How many of the choices a quotation takes. The minimum follows it. */
  maxSelections: string
  extraItemPrice: string
  allowExtra: boolean
  menuItemIds: string[]
  mandatoryIds: string[]
  /** Serving fraction per chosen dish, keyed by menu item id. */
  portions: Record<string, number>
  /** A note about that dish on this package, keyed the same way. */
  remarks: Record<string, string>
}

/**
 * Edits an existing package.
 *
 * <p>A published version is never edited in place — it is what quotations were
 * priced from, and moving it would move prices under quotations already sent.
 * Opening this on a published package therefore opens a <em>new draft</em>
 * carrying its items and prices across, which is published from the list when
 * it is ready. A package still in draft is edited where it stands.</p>
 */
export function PackageEditDialog(props: {
  pkg: CateringPackage | null
  onClose: () => void
}) {
  const { pkg } = props
  const editPackage = useEditPackage()
  const saveVersion = useSavePackageVersion()
  const { data: outlets } = useOutlets()
  // Asks the server which version an edit should be written to, and opens a new
  // draft from the published one when that is what is needed.
  const { data: version, isLoading } = useEditableVersion(pkg?.id)

  const [name, setName] = useState('')
  // A package built once and offered under several lines of business, same
  // menu and rate throughout, rather than copied once per service.
  const [serviceTypes, setServiceTypes] = useState<string[]>(['BANQUET'])
  const [sessionType, setSessionType] = useState('ANY')
  const [outletId, setOutletId] = useState('')
  // What this outlet actually sells, from the service master. A package for a
  // service the outlet does not offer is a package nobody can quote.
  const { data: availableServiceTypes } = useServiceTypes({ outletId: outletId || undefined })
  const services = (availableServiceTypes ?? []).filter((type) => type.bookable)
  const selectedServices = services.filter((type) => serviceTypes.includes(type.code))
  // A banquet or a restaurant party books a space of ours; an outdoor job or a
  // takeaway has none. Offered under several services at once, it books one
  // the moment any of them does.
  const booksASpace = selectedServices.some((type) => Boolean(type.spaceType))
  const spaceTypesInUse = new Set(
    selectedServices.filter((type) => type.spaceType).map((type) => type.spaceType),
  )
  // Halls and dining rooms are both spaces, but different ones. Offered under
  // only one kind at once, the picker narrows to it; offered under both, it
  // shows every space rather than guessing which was meant.
  const spaceType = spaceTypesInUse.size === 1
    ? ((spaceTypesInUse.values().next().value === 'DINING' ? 'DINING' : 'HALL') as 'HALL' | 'DINING')
    : undefined
  const { data: halls } = useHalls(outletId || undefined, booksASpace ? spaceType : undefined)

  // Moving to an outlet that does not sell what was chosen would leave the
  // field naming a service the outlet cannot deliver.
  useEffect(() => {
    if (services.length === 0) return
    const stillOffered = serviceTypes.filter((code) => services.some((type) => type.code === code))
    if (stillOffered.length === 0) {
      setServiceTypes([services[0].code])
    } else if (stillOffered.length !== serviceTypes.length) {
      setServiceTypes(stillOffered)
    }
  }, [services, serviceTypes])

  // The sittings this line of business is sold by, off the master. A package
  // already sold under a sitting since withdrawn or unassigned keeps naming it:
  // correcting that silently on open would change what the office agreed.
  // Offered under more than one service, every sitting shows rather than
  // narrowing to just one service's.
  const { data: sessionTypes } = useSessionTypes({
    serviceType: serviceTypes.length === 1 ? serviceTypes[0] : undefined,
  })
  const sittings = sessionTypes ?? []
  const sittingKnown =
    sessionType === 'ANY' || sittings.some((sitting) => sitting.code === sessionType)

  const [hallIds, setHallIds] = useState<string[]>([])
  const [active, setActive] = useState(true)

  const [basePricePerPerson, setBasePrice] = useState('')
  const [nameLocal, setNameLocal] = useState('')
  const [taxRatePercent, setTaxRate] = useState('')
  // Whether the per-head price above already has that GST in it. "Four hundred
  // a head, all in" and "four hundred plus tax" are different numbers, and the
  // customer was told one of them.
  const [pricing, setPricing] = useState<'DEFAULT' | 'INCLUSIVE' | 'EXCLUSIVE'>('DEFAULT')
  const priceInclusiveTax = pricing === 'DEFAULT' ? undefined : pricing === 'INCLUSIVE'
  const [items, setItems] = useState<ItemDraft[]>([])
  const [loadedVersion, setLoadedVersion] = useState<string | null>(null)

  const { known, remember } = useDishMemory()

  useEffect(() => {
    if (!pkg) return
    setName(pkg.name)
    setNameLocal(pkg.nameLocal ?? '')
    setServiceTypes(pkg.serviceTypes)
    setSessionType(pkg.sessionType ?? 'ANY')
    setOutletId(pkg.outletId ?? '')
    setHallIds(pkg.hallIds ?? [])
    setActive(pkg.active)
  }, [pkg])

  useEffect(() => {
    if (!version || loadedVersion === version.id) return
    setLoadedVersion(version.id)
    // What the package already offers, named by the package itself. Without
    // this the items would open empty until somebody searched for each dish.
    remember(
      version.items.flatMap((item) =>
        item.choices.map((choice) => ({
          id: choice.menuItemId,
          itemCode: choice.itemCode,
          name: choice.name,
        })),
      ),
    )
    setBasePrice(String(version.basePricePerPerson ?? ''))
    setTaxRate(String(version.taxRatePercent ?? ''))
    setPricing(
      version.priceInclusiveTax == null ? 'DEFAULT' : version.priceInclusiveTax ? 'INCLUSIVE' : 'EXCLUSIVE',
    )
    setItems(
      version.items.map((item) => ({
        itemNumber: item.itemNumber,
        itemLabel: item.itemLabel,
        maxSelections: String(item.maxSelections),
        extraItemPrice: String(item.extraItemPrice ?? 0),
        allowExtra: item.allowExtra,
        menuItemIds: item.choices.map((choice) => choice.menuItemId),
        mandatoryIds: item.choices.filter((choice) => choice.mandatory).map((c) => c.menuItemId),
        portions: Object.fromEntries(
          item.choices.map((choice) => [choice.menuItemId, nearestPortion(choice.portion)]),
        ),
        remarks: Object.fromEntries(
          item.choices.map((choice) => [choice.menuItemId, choice.remarks ?? '']),
        ),
      })),
    )
  }, [version, loadedVersion, remember])

  const updateItem = (index: number, patch: Partial<ItemDraft>) =>
    setItems((current) => current.map((item, position) => (position === index ? { ...item, ...patch } : item)))

  const submit = () => {
    if (!pkg || !version) return
    editPackage.mutate(
      {
        id: pkg.id,
        body: {
          name: name.trim(),
          nameLocal: nameLocal.trim() || undefined,
          serviceTypes,
          sessionType,
          outletId: outletId || undefined,
          hallIds: booksASpace ? hallIds : undefined,
          active,
        },
      },
      {
        onSuccess: () =>
          saveVersion.mutate(
            {
              packageId: pkg.id,
              versionId: version.id,
              body: {
                // The guarantee, child percentage and complimentary guests are
                // deliberately not sent: they are no longer edited here, and the
                // server leaves a field it is not given exactly as it stands.
                basePricePerPerson: Number(basePricePerPerson || 0),
                taxRatePercent: Number(taxRatePercent || 0),
                priceInclusiveTax,
                items: items
                  .filter((item) => item.menuItemIds.length > 0)
                  .map((item, index) => ({
                    itemNumber: index + 1,
                    itemLabel: item.itemLabel.trim() || `Item ${index + 1}`,
                    // One number: an item is taken exactly this many times.
                    minSelections: Number(item.maxSelections || 0),
                    maxSelections: Number(item.maxSelections || 0),
                    extraItemPrice: Number(item.extraItemPrice || 0),
                    allowExtra: item.allowExtra,
                    choices: item.menuItemIds.map((menuItemId) => ({
                      menuItemId,
                      mandatory: item.mandatoryIds.includes(menuItemId),
                      portion: item.portions[menuItemId] ?? WHOLE_PORTION,
                      remarks: item.remarks[menuItemId] || undefined,
                    })),
                  })),
              },
            },
            { onSuccess: close },
          ),
      },
    )
  }

  const close = () => {
    setLoadedVersion(null)
    props.onClose()
  }

  const busy = editPackage.isPending || saveVersion.isPending

  return (
    <Dialog open={Boolean(pkg)} onClose={close} maxWidth="lg" fullWidth>
      <DialogTitle>
        {pkg?.name}
        <Typography variant="caption" color="text.secondary" display="block">
          {pkg?.code}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={editPackage.error ?? saveVersion.error} />

        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Opening the version to edit…
          </Typography>
        ) : null}

        {version ? (
          <Alert severity={version.versionNumber > 1 ? 'warning' : 'info'} sx={{ mb: 2 }}>
            Editing <strong>version {version.versionNumber}</strong>, which is a draft. Publish it
            from the package list to put it on sale — until then, quotations keep using the version
            that is live.
          </Alert>
        ) : null}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Package
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Grid>
          {/* The heading on a menu sheet printed in Tamil. Left empty, the
              sheet prints the name above - a catalogue nobody has translated
              yet still prints. */}
          <Grid item xs={12} md={4}>
            <TamilNameField value={nameLocal} onChange={setNameLocal} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              fullWidth
              options={services.map((type) => type.code)}
              value={serviceTypes.filter((code) => services.some((type) => type.code === code))}
              onChange={(_event, value) => setServiceTypes(value)}
              getOptionLabel={(code) => services.find((type) => type.code === code)?.name ?? code}
              renderTags={(value, getTagProps) =>
                value.map((code, index) => (
                  <Chip size="small" label={services.find((type) => type.code === code)?.name ?? code}
                        {...getTagProps({ index })} key={code} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} required={serviceTypes.length === 0} label="Service" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Session"
              value={sessionType}
              onChange={(event) => setSessionType(event.target.value)}
            >
              <MenuItem value="ANY">Any</MenuItem>
              {sittings.map((sitting) => (
                <MenuItem key={sitting.code} value={sitting.code}>
                  {sitting.name}
                </MenuItem>
              ))}
              {/* Sold under a sitting since withdrawn: still shown, so the
                  field is not blank and saving does not quietly rewrite it. */}
              {sittingKnown ? null : (
                <MenuItem value={sessionType}>{sessionType} (withdrawn)</MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={(event) => setActive(event.target.checked)} />
              }
              label={active ? 'On offer' : 'Withdrawn'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Outlet"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
              helperText="Blank offers it at every outlet"
            >
              <MenuItem value="">All outlets</MenuItem>
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* A banquet or a restaurant party is sold against a space of ours;
              the other services have none. */}
          {booksASpace && (
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                fullWidth
                options={(halls ?? []).map((hall) => hall.id)}
                value={hallIds.filter((id) => (halls ?? []).some((hall) => hall.id === id))}
                onChange={(_event, value) => setHallIds(value)}
                getOptionLabel={(id) => halls?.find((hall) => hall.id === id)?.name ?? id}
                renderTags={(value, getTagProps) =>
                  value.map((id, index) => (
                    <Chip size="small" label={halls?.find((hall) => hall.id === id)?.name ?? id}
                          {...getTagProps({ index })} key={id} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={spaceType === 'DINING' ? 'Dining rooms' : spaceType === 'HALL' ? 'Halls' : 'Spaces'}
                    helperText="Blank offers it everywhere"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Pricing
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Per person"
              value={basePricePerPerson}
              onChange={(event) => setBasePrice(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="GST %"
              value={taxRatePercent}
              onChange={(event) => setTaxRate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Price is"
              value={pricing}
              onChange={(event) =>
                setPricing(event.target.value as 'DEFAULT' | 'INCLUSIVE' | 'EXCLUSIVE')
              }
            >
              <MenuItem value="DEFAULT">As the company sets</MenuItem>
              <MenuItem value="EXCLUSIVE">Excluding GST</MenuItem>
              <MenuItem value="INCLUSIVE">Including GST</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Items</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              setItems((current) => [
                ...current,
                {
                  itemNumber: current.length + 1,
                  itemLabel: '',
                  maxSelections: '1',
                  extraItemPrice: '0',
                  allowExtra: false,
                  menuItemIds: [],
                  mandatoryIds: [],
                  portions: {},
                  remarks: {},
                },
              ])
            }
          >
            Add item
          </Button>
        </Stack>

        <Stack spacing={2} sx={{ mt: 1 }}>
          {items.map((item, index) => {
            const chosen = item.menuItemIds
              .map((id) => known.get(id))
              .filter((dish): dish is Dish => Boolean(dish))
            return (
              <Card key={index} variant="outlined">
                <CardContent>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={1}>
                      <Chip label={`Item ${index + 1}`} color="primary" size="small" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Called"
                        value={item.itemLabel}
                        onChange={(event) => updateItem(index, { itemLabel: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="How many chosen"
                        value={item.maxSelections}
                        onChange={(event) => updateItem(index, { maxSelections: event.target.value })}
                        helperText="Exactly this many"
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Extra item price"
                        value={item.extraItemPrice}
                        onChange={(event) => updateItem(index, { extraItemPrice: event.target.value })}
                        disabled={!item.allowExtra}
                      />
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={item.allowExtra}
                            onChange={(event) => updateItem(index, { allowExtra: event.target.checked })}
                          />
                        }
                        label="Extra"
                      />
                    </Grid>
                    <Grid item xs={12} md={1} sx={{ textAlign: 'right' }}>
                      <IconButton
                        size="small"
                        onClick={() => setItems((c) => c.filter((_, position) => position !== index))}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>

                    <Grid item xs={12}>
                      <DishPicker
                        label={`Choices for item ${index + 1}`}
                        chosen={chosen}
                        onChange={(value) => {
                          remember(value)
                          updateItem(index, {
                            menuItemIds: value.map((option) => option.id),
                            mandatoryIds: item.mandatoryIds.filter((id) =>
                              value.some((option) => option.id === id),
                            ),
                            // A dish just added is a whole serving until told
                            // otherwise; one removed takes its portion with it.
                            portions: Object.fromEntries(
                              value.map((option) => [
                                option.id,
                                item.portions[option.id] ?? WHOLE_PORTION,
                              ]),
                            ),
                            remarks: Object.fromEntries(
                              value.map((option) => [option.id, item.remarks[option.id] ?? '']),
                            ),
                          })
                        }}
                      />
                    </Grid>

                    {chosen.length > 0 ? (
                      <>
                        <Grid item xs={12}>
                          <PortionList
                            chosen={chosen}
                            portions={item.portions}
                            remarks={item.remarks}
                            onChange={(portions) => updateItem(index, { portions })}
                            onRemarksChange={(remarks) => updateItem(index, { remarks })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            multiple
                            size="small"
                            options={chosen}
                            value={chosen.filter((dish) => item.mandatoryIds.includes(dish.id))}
                            getOptionLabel={(option) => option.name}
                            onChange={(_event, value) =>
                              updateItem(index, { mandatoryIds: value.map((option) => option.id) })
                            }
                            renderInput={(params) => (
                              <TextField {...params} label="Always included (not optional)" />
                            )}
                          />
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                </CardContent>
              </Card>
            )
          })}
        </Stack>

        {basePricePerPerson ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            {pricing === 'INCLUSIVE'
              ? `${formatMoney(Number(basePricePerPerson))} per head, GST included.`
              : `${formatMoney(Number(basePricePerPerson))} per head, plus ${taxRatePercent || 0}% GST.`}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !version || !name.trim() || serviceTypes.length === 0}
        >
          Save draft
        </Button>
      </DialogActions>
    </Dialog>
  )
}
